import { useState } from "react";
import { Form, Input, Drawer, Skeleton } from "../../components/ui";
import DataTableCards from "../../components/DataTable/DataTableCards";
import dayjs from "dayjs";
import swal from "../../utils/swal";
import { Tag, Plus, Pencil, Trash2, Search, SearchX } from "lucide-react";
import {
  useGetAllCategories,
  useAddCategory,
  useUpdateCategory,
  useDeleteCategory,
  useToggleCategoryStatus,
} from "../../query/categoryQuery";
import { useDebouncedValue } from "../../hooks/useDebouncedValue";
import "../teachers/teachers.css";
import "./categories.css";

function statusTone(status) {
  return status === "ACTIVE" ? "is-active" : "is-inactive";
}

function statusLabel(status) {
  return status === "ACTIVE" ? "Active" : "Inactive";
}

function Categories() {
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [editingCategory, setEditingCategory] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [form] = Form.useForm();
  const debouncedKeyword = useDebouncedValue(searchTerm.trim(), 400);

  const { isLoading, isFetching, isError, error, data: categoryData } = useGetAllCategories({
    page: 1,
    limit: 100,
    keyword: debouncedKeyword,
  });
  const categories = categoryData?.docs || [];

  const { mutate: addCategory, isPending: isAdding } = useAddCategory();
  const { mutate: updateCategory, isPending: isUpdating } = useUpdateCategory();
  const { mutate: deleteCategory } = useDeleteCategory();
  const { mutate: toggleStatus, isPending: isUpdatingStatus } = useToggleCategoryStatus();

  const isMutating = isAdding || isUpdating;
  const isEdit = Boolean(editingCategory);

  const openAdd = () => {
    setEditingCategory(null);
    form.resetFields();
    setDrawerOpen(true);
  };

  const openEdit = (item) => {
    setEditingCategory(item);
    form.setFieldsValue({
      title: item.title || "",
      description: item.description || "",
    });
    setDrawerOpen(true);
  };

  const closeDrawer = () => {
    setDrawerOpen(false);
    setEditingCategory(null);
    form.resetFields();
  };

  const handleSubmit = () => {
    form.validateFields().then((values) => {
      const payload = {
        title: values.title?.trim(),
        description: values.description?.trim(),
      };

      if (isEdit) {
        updateCategory(
          { id: editingCategory._id, ...payload },
          {
            onSuccess: () => {
              swal("Success", "Category updated successfully", "success");
              closeDrawer();
            },
            onError: (error) =>
              swal("Error", error?.message || "Failed to update category", "error"),
          }
        );
        return;
      }

      addCategory(payload, {
        onSuccess: () => {
          swal("Success", "Category added successfully", "success");
          closeDrawer();
        },
        onError: (error) => swal("Error", error?.message || "Failed to add category", "error"),
      });
    });
  };

  const handleDelete = (item) => {
    swal({
      title: "Are you sure?",
      text: `Delete "${item.title || "this category"}"? Items using it may need reassignment.`,
      icon: "warning",
      buttons: ["No, cancel it!", "Yes, I am sure!"],
      dangerMode: true,
    }).then((isConfirm) => {
      if (!isConfirm) return;
      deleteCategory(item._id, {
        onSuccess: () => swal("Success", "Category deleted successfully", "success"),
        onError: (error) =>
          swal("Cannot delete", error?.message || "Failed to delete category", "error"),
      });
    });
  };

  const handleStatus = (item) => {
    const next = item.status === "ACTIVE" ? "Inactive" : "Active";
    swal({
      title: "Are you sure?",
      text: `Set "${item.title}" to ${next}?`,
      icon: "warning",
      buttons: ["No, cancel it!", "Yes, I am sure!"],
    }).then((isConfirm) => {
      if (!isConfirm) return;
      toggleStatus(item._id, {
        onSuccess: () => swal("Success", `Status updated to ${next}`, "success"),
        onError: (error) => swal("Error", error?.message || "Failed to update status", "error"),
      });
    });
  };

  return (
    <div className="data-page categories-page">
      <div className="data-toolbar">
        <div className="data-toolbar-search">
          <label className="data-search">
            <Search size={16} />
            <input
              placeholder="Search categories..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
            {isFetching && searchTerm ? <span className="data-search-spinner" aria-hidden /> : null}
          </label>
        </div>

        <div className="data-toolbar-actions">
          <button type="button" className="data-primary" onClick={openAdd}>
            <Plus size={18} />
            Add Category
          </button>
        </div>
      </div>

      <div className="data-card">
        {isError ? (
          <div className="data-empty">
            <span className="data-empty-icon cat-empty-icon">
              <SearchX size={36} strokeWidth={1.6} />
            </span>
            <h3>Could not load categories</h3>
            <p>{error?.message || "Something went wrong while fetching categories."}</p>
          </div>
        ) : isLoading && !categories.length ? (
          <div style={{ padding: 24 }}>
            <Skeleton active />
          </div>
        ) : categories.length === 0 ? (
          <div className="data-empty">
            <span className="data-empty-icon cat-empty-icon">
              {debouncedKeyword ? <SearchX size={36} strokeWidth={1.6} /> : <Tag size={36} strokeWidth={1.6} />}
            </span>
            <h3>{debouncedKeyword ? "No categories found" : "No categories yet"}</h3>
            <p>
              {debouncedKeyword
                ? "Try a different search term."
                : "Add a category to use when creating inventory items."}
            </p>
            {!debouncedKeyword ? (
              <button type="button" className="data-primary" onClick={openAdd}>
                <Plus size={18} />
                Add Category
              </button>
            ) : null}
          </div>
        ) : (
          <DataTableCards fetching={isFetching || isMutating || isUpdatingStatus}>
            <table className="data-table">
              <thead>
                <tr>
                  <th>Category</th>
                  <th>Description</th>
                  <th>Status</th>
                  <th>Created</th>
                  <th>Action</th>
                </tr>
              </thead>
              <tbody>
                {categories.map((item) => (
                  <tr key={item._id}>
                    <td>
                      <div className="data-person">
                        <span className="cat-avatar" aria-hidden>
                          <Tag size={18} />
                        </span>
                        <p className="data-name">{item.title || "—"}</p>
                      </div>
                    </td>
                    <td className="cat-desc-cell">{item.description || "—"}</td>
                    <td>
                      <button
                        type="button"
                        className={`data-pill data-status-trigger ${statusTone(item.status)}`}
                        onClick={() => handleStatus(item)}
                        aria-label={`Change status for ${item.title}`}
                      >
                        {statusLabel(item.status)}
                      </button>
                    </td>
                    <td>{item.createdAt ? dayjs(item.createdAt).format("MMM D, YYYY") : "—"}</td>
                    <td>
                      <div className="cat-row-actions">
                        <button
                          type="button"
                          className="cat-action-btn is-edit"
                          aria-label={`Edit ${item.title}`}
                          onClick={() => openEdit(item)}
                        >
                          <Pencil size={18} />
                        </button>
                        <button
                          type="button"
                          className="cat-action-btn is-delete"
                          aria-label={`Delete ${item.title}`}
                          onClick={() => handleDelete(item)}
                        >
                          <Trash2 size={18} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </DataTableCards>
        )}
      </div>

      <Drawer
        open={drawerOpen}
        onClose={closeDrawer}
        placement="right"
        title={isEdit ? "Edit Category" : "Add Category"}
        width={400}
        footer={
          <>
            <button type="button" className="bw-drawer-btn is-ghost" onClick={closeDrawer} disabled={isMutating}>
              Cancel
            </button>
            <button
              type="button"
              className="bw-drawer-btn is-primary cat-save-btn"
              onClick={handleSubmit}
              disabled={isMutating}
            >
              {isMutating ? "Saving..." : isEdit ? "Update" : "Save"}
            </button>
          </>
        }
      >
        <Form form={form} layout="vertical" className="cat-drawer-form" onFinish={handleSubmit}>
          <div className="cat-drawer-field">
            <p className="cat-drawer-label">
              Title<span>*</span>
            </p>
            <Form.Item
              name="title"
              className="cat-form-item"
              rules={[{ required: true, message: "Please enter a category title" }]}
            >
              <Input placeholder="e.g. Stationery" />
            </Form.Item>
          </div>

          <div className="cat-drawer-field">
            <p className="cat-drawer-label">
              Description<span>*</span>
            </p>
            <Form.Item
              name="description"
              className="cat-form-item"
              rules={[{ required: true, message: "Please enter a description" }]}
            >
              <Input.TextArea placeholder="Brief description of this category" rows={4} />
            </Form.Item>
          </div>
        </Form>
      </Drawer>
    </div>
  );
}

export default Categories;
